import type { TExample } from '@atlas/uicorn/preview/modules';
import { useMemo } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import { Form as AntForm, type FormItemProps as TAntFormItemProps } from 'antd';
import Text from '@atlas/uicorn/lib/Display/Text';
import Input, { SInput, SInputMultiline } from '@atlas/uicorn/lib/Input/Input';
import isNotNullable from '@atlas/tool/box/isNotNullable';
import { SRange } from '@atlas/uicorn/lib/Input/Range';
import { SColorInput } from '@atlas/uicorn/lib/Input/ColorInput';
import cn from 'classnames';
import { SSelect } from '@atlas/uicorn/lib/Input/Select';
import type { Rule as TAntRule } from 'antd/lib/form';
import type { RuleObject as TAntRuleObject } from 'antd/lib/form';
import type { NamePath as TAntNamePath } from 'antd/lib/form/interface';
import { SRichInput } from '@atlas/uicorn/lib/Input/RichInput';

const FormItem = React.memo(function FormItem(props: TFormItemProps): React.ReactElement {
    const {
        label,
        description,
        className,
        shrink = false,
        expand = false,
        layout,
        error,
        large,
        ...formItemProps
    } = props;

    const inputLabel = useMemo(() => {
        const isNullableLabel = !isNotNullable(label);
        const isNullableDescription = !isNotNullable(description);

        if (isNullableLabel && isNullableDescription) return null;

        return (
            <SFormItemLabel>
                {!isNullableLabel && <SFormItemLabelText children={label} />}
                {!isNullableDescription && <SFormItemLabelExtra children={description} />}
            </SFormItemLabel>
        );
    }, [label, description]);

    const isEmpty = React.Children.count(props.children) === 0;

    return (
        <SFormItem
            colon={false}
            label={inputLabel}
            {...formItemProps}
            className={cn(className, {
                ['atlas-form-item-shrink']: shrink,
                ['atlas-form-item-vertical']: layout === 'vertical',
            })}
            $expand={expand}
            $empty={isEmpty}
            $large={large}
            {...(error && {
                help: error,
                validateStatus: 'error',
            })}
        />
    );
});

function useFormItemStatus(): 'success' | 'warning' | 'error' | 'validating' | '' | undefined {
    const { status } = AntForm.Item.useStatus();
    return status;
}

export default Object.assign(FormItem, {
    useStatus: useFormItemStatus,
});

// #region Styles

const SFormItemLabelExtra = styled(Text).attrs({ caption: true })`
    display: inline-block;
    color: ${(p) => p.theme.color.grey8};
`;

export const SFormItemLabelText = styled(Text).attrs({ h: '5' })`
    line-height: 1;

    & + ${SFormItemLabelExtra} {
        margin-top: 0.4rem;
    }
`;

const SFormItemLabel = styled.div`
    white-space: pre-wrap;
    color: ${(p) => p.theme.color.grey9};
`;

export const SFormItem = styled(AntForm.Item)<{ $empty: boolean; $expand: boolean; $large?: boolean }>`
    margin-bottom: unset;
    color: ${(p) => p.theme.color.grey9};

    .iti {
        // intl-tel-input package input wrapper
        width: 100%;
    }

    ${SSelect}, ${SRange}, ${SColorInput} {
        width: 100%;
    }

    .ant-form-item-explain,
    .ant-form-item-extra {
        margin-top: 0.4rem;
        font: ${(p) => p.theme.font.caption};
    }

    .ant-form-item-extra {
        color: ${(p) => p.theme.color.grey8};
    }

    .ant-form-item-explain-error {
        color: ${(p) => p.theme.color.red7};
    }

    ${(p) =>
        p.$empty
            ? css`
                  .ant-form-item-control-input {
                      display: none;
                  }
              `
            : ''}

    ${(p) =>
        p.$expand
            ? css`
                  .ant-form-item-row {
                      flex-direction: column;
                      > .ant-form-item-label,
                      > .ant-form-item-control {
                          width: 100%;
                      }
                  }
              `
            : ''}

    ${(p) =>
        p.theme.when.screenVariant(
            'tiny',
            css`
                .ant-form-item-row {
                    flex-direction: column;
                }
            `,
        )}

    ${(p) =>
        p.$large
            ? css`
                  ${SInput}, ${SInputMultiline}, ${SRichInput} {
                      font-size: 2.4rem;
                      font-weight: 600;
                  }
              `
            : ''}
`;

// #endregion

// #region Types

export type TFormItemRule = TAntRule;
export type TFormItemRuleObject = TAntRuleObject;

export type TFormItemNamePath = TAntNamePath;

export type TFormItemProps = Pick<
    TAntFormItemProps,
    | 'className'
    | 'style'
    | 'children'
    | 'label'
    | 'help'
    | 'extra'
    | 'initialValue'
    | 'required'
    | 'hidden'
    | 'normalize'
    | 'dependencies'
    | 'getValueFromEvent'
    | 'valuePropName'
    | 'htmlFor'
    | 'messageVariables'
    | 'noStyle'
> & {
    name?: TFormItemNamePath;
    description?: React.ReactNode;
    shrink?: boolean;
    expand?: boolean;
    layout?: 'horizontal' | 'vertical';
    rules?: TFormItemRule[];
    error?: React.ReactNode;
    large?: boolean;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <FormItem
                label={props.label}
                description={props.description}
                help={props.help}
                initialValue={props.initialValue}
                hidden={props.hidden}
            >
                <Input />
            </FormItem>
        );
    },
    controls: {
        label: 'string',
        description: 'string',
        help: 'string',
        initialValue: 'string',
        hidden: 'boolean',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?type=design&node-id=6-891&mode=design&t=dygdJrYUTAzsGhAY-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('FormItem', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
